/**
 * Admin — 统一 TP 费用（血缘转出手续费 %）
 */
import { useEffect } from 'react';
import { Alert, Button, Card, Form, InputNumber, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface TpFeeSettings {
  tp_fee_pct_a3: string;
  apply_from_level: string;
  free_from_level: string;
  config_key: string;
}

export function UnifiedTpFeePage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('system.config.edit');
  const [form] = Form.useForm();

  const query = useQuery({
    queryKey: ['admin', 'tp-fee'],
    queryFn: () => api.get<TpFeeSettings>('/api/admin/ops-config/tp-fee/settings'),
  });

  useEffect(() => {
    if (query.data) {
      form.setFieldsValue({
        tp_fee_pct_a3: Number(query.data.tp_fee_pct_a3),
      });
    }
  }, [query.data, form]);

  const save = useMutation({
    mutationFn: (body: { tp_fee_pct_a3: number }) =>
      api.put<TpFeeSettings>('/api/admin/ops-config/tp-fee/settings', body),
    onSuccess: (data) => {
      message.success('统一 TP 费用已保存');
      form.setFieldsValue({ tp_fee_pct_a3: Number(data.tp_fee_pct_a3) });
      qc.setQueryData(['admin', 'tp-fee'], data);
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : '保存失败'),
  });

  const applyFrom = query.data?.apply_from_level ?? 'A3';
  const freeFrom = query.data?.free_from_level ?? 'A5';

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        统一 TP 费用
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${applyFrom}～A4 血缘转出 TP 收取统一手续费百分比；${freeFrom} 及以上免手续费。`}
      />
      <Card
        loading={query.isLoading}
        extra={
          canEdit ? (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              保存
            </Button>
          ) : null
        }
      >
        <Form form={form} layout="vertical" disabled={!canEdit} onFinish={(v) => save.mutate(v)}>
          <Form.Item
            name="tp_fee_pct_a3"
            label={`TP 手续费（${applyFrom}～A4）`}
            rules={[{ required: true, message: '请输入手续费百分比' }]}
            extra={`例：填 10 表示收取 10%；${freeFrom}+ 始终为 0`}
          >
            <InputNumber min={0} max={100} step={0.1} style={{ width: 220 }} addonAfter="%" />
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}

export default UnifiedTpFeePage;
